import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ClipboardList, Search, Lock, MapPin, Clock, Sparkles, Phone, MessageCircle, Send, ArrowLeft, FileText, CalendarCheck, Users, Languages, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { DISTRICTS, CATEGORIES, HOURS, EXPERIENCE, STATUSES, fmtDate, fmtDateTime } from "../lib/options";
import { Empty, Spinner, Chip, PageTitle } from "../components/Bits";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const MatchBar = ({ m }) => {
  const { t } = useT();
  return (
    <div data-testid="match-indicator">
      <div className="flex items-center justify-between text-sm font-bold"><span>{t("match")}</span><span className="text-[color:var(--orange-dark)]">{m.score}%</span></div>
      <div className="mt-1 h-2 rounded-full bg-slate-200"><div className="h-full rounded-full bg-[color:var(--orange)]" style={{ width: `${m.score}%` }} /></div>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {(m.reasons.length ? m.reasons : ["none"]).map((r) => <li key={r} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{t(`r_${r}`)}</li>)}
      </ul>
    </div>
  );
};

const CandSummary = ({ c }) => {
  const { t } = useT();
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
      <span className="inline-flex items-center gap-1"><MapPin size={14} />{c.location || "—"}</span>
      <span className="inline-flex items-center gap-1"><Sparkles size={14} />{c.experience ? t(`e_${c.experience}`) : "—"}</span>
      <span className="inline-flex items-center gap-1"><Clock size={14} />{(c.hours || []).map((h) => t(`h_${h}`)).join(", ") || "—"}</span>
      {c.availability && <span className="inline-flex items-center gap-1"><CalendarCheck size={14} />{t(`a_${c.availability}`)}</span>}
    </div>
  );
};

export function EmployerApplications() {
  const { t, lang } = useT();
  const [sp, setSp] = useSearchParams();
  const [apps, setApps] = useState(null);
  const status = sp.get("status") || "";
  useEffect(() => {
    setApps(null);
    const params = {};
    if (sp.get("job_id")) params.job_id = sp.get("job_id");
    if (status) params.status = status;
    api.get("/employer/applications", { params }).then((r) => setApps(r.data)).catch((e) => { toast.error(errMsg(e)); setApps([]); });
  }, [sp, status]);
  const setStatus = (s) => { const n = new URLSearchParams(sp); s ? n.set("status", s) : n.delete("status"); setSp(n); };
  return (
    <div className="page pb-12">
      <Link to="/employer" className="mt-4 inline-flex min-h-[44px] items-center gap-2 font-semibold text-slate-600" data-testid="apps-back-btn"><ArrowLeft size={20} />{t("back")}</Link>
      <PageTitle>{t("apps_emp_t")}</PageTitle>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2" data-testid="apps-status-filter">
        {["", ...STATUSES].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`apps-filter-${s || "all"}`}
            className={`min-h-[44px] shrink-0 rounded-full border-2 px-4 font-bold ${status === s ? "border-[color:var(--navy)] bg-[color:var(--navy)] text-white" : "border-slate-200 bg-white text-slate-700"}`}>
            {s ? t(`est_${s}`) : t("all")}
          </button>
        ))}
      </div>
      <div className="mt-4">
        {!apps ? <Spinner /> : apps.length === 0 ? <Empty icon={ClipboardList} title={t("no_emp_apps_t")} sub={t("no_emp_apps_sub")} testid="emp-apps-empty" /> : (
          <div className="grid gap-3 md:grid-cols-2">
            {apps.map((a) => (
              <Link key={a.id} to={`/employer/candidates/${a.candidate_id}?app=${a.id}`} className="card-soft block space-y-3 p-4 hover:shadow-md" data-testid={`emp-app-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><p className="text-lg font-bold">{a.candidate.full_name}</p><p className="text-sm text-slate-600">{a.jobs?.title} · {fmtDate(a.created_at, lang)}</p></div>
                  <Chip status={a.status} label={t(`est_${a.status}`)} />
                </div>
                <CandSummary c={a.candidate} />
                <MatchBar m={a.match} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const sel = "field !min-h-[52px] !text-base";

export function CandidateSearch() {
  const { t } = useT();
  const [f, setF] = useState({ location: "", job_type: "", experience: "", hours: "" });
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/employer/candidates", { params }).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setD({ results: [] }); });
  }, [f]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="page pb-12">
      <Link to="/employer" className="mt-4 inline-flex min-h-[44px] items-center gap-2 font-semibold text-slate-600" data-testid="search-back-btn"><ArrowLeft size={20} />{t("back")}</Link>
      <PageTitle>{t("search_t")}</PageTitle>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <select className={sel} value={f.location} onChange={set("location")} data-testid="search-filter-location" aria-label={t("lives")}><option value="">{t("place_l")}: {t("any")}</option>{DISTRICTS.map((x) => <option key={x}>{x}</option>)}</select>
        <select className={sel} value={f.job_type} onChange={set("job_type")} data-testid="search-filter-type" aria-label={t("pj_cat")}><option value="">{t("all_types")}</option>{CATEGORIES.map(({ v }) => <option key={v} value={v}>{t(`cat_${v}`)}</option>)}</select>
        <select className={sel} value={f.experience} onChange={set("experience")} data-testid="search-filter-experience" aria-label={t("experience_l")}><option value="">{t("experience_l")}: {t("any")}</option>{EXPERIENCE.map((v) => <option key={v} value={v}>{t(`e_${v}`)}</option>)}</select>
        <select className={sel} value={f.hours} onChange={set("hours")} data-testid="search-filter-hours" aria-label={t("hours_l")}><option value="">{t("hours_l")}: {t("any")}</option>{HOURS.map(({ v }) => <option key={v} value={v}>{t(`h_${v}`)}</option>)}</select>
      </div>
      <div className="mt-6">
        {!d ? <Spinner /> : d.locked ? (
          <Empty icon={Lock} title={t("search_locked_t")} sub={`${d.total} ${t("search_locked_sub")}`} testid="search-locked">
            <Link to="/employer/subscription" className="btn-orange" data-testid="search-choose-plan-btn">{t("choose_plan")}</Link>
          </Empty>
        ) : d.results.length === 0 ? <Empty icon={Users} title={t("no_cands_t")} sub={t("no_cands_sub")} testid="search-empty" /> : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="search-results">
            {d.results.map((c) => (
              <Link key={c.user_id} to={`/employer/candidates/${c.user_id}`} className="card-soft block space-y-3 p-4 hover:shadow-md" data-testid={`search-result-${c.user_id}`}>
                <p className="text-lg font-bold">{c.full_name}</p>
                <CandSummary c={c} />
                <MatchBar m={c.match} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPanel({ app, onChange }) {
  const { t } = useT();
  const [ask, setAsk] = useState(null);
  const change = async (s) => {
    try { const { data } = await api.put(`/employer/applications/${app.id}/status`, { status: s }); onChange(data); toast.success(t("saved_ok")); } catch (e) { toast.error(errMsg(e)); }
    setAsk(null);
  };
  return (
    <div className="card-soft p-4" data-testid="status-panel">
      <p className="mb-3 font-bold text-[color:var(--navy)]">{t("change_status")}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => (["hired", "not_selected"].includes(s) ? setAsk(s) : change(s))} data-testid={`status-btn-${s}`}
            className={`min-h-[52px] rounded-xl border-2 px-2 font-bold ${app.status === s ? "border-[color:var(--orange)] bg-[color:var(--orange)] text-white" : "border-slate-200 bg-white"}`}>
            {t(`est_${s}`)}
          </button>
        ))}
      </div>
      <AlertDialog open={!!ask} onOpenChange={() => setAsk(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="status-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>{t("confirm_status_q")} « {ask && t(`est_${ask}`)} » ?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="min-h-[48px]" data-testid="status-confirm-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction className="min-h-[48px] bg-[color:var(--orange)]" onClick={() => change(ask)} data-testid="status-confirm-ok">{t("confirm_status")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InterviewAndNotes({ app, onChange }) {
  const { t, lang } = useT();
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [when, setWhen] = useState("");
  const [place, setPlace] = useState(app.interview_place || "");
  useEffect(() => { api.get(`/employer/applications/${app.id}/notes`).then((r) => setNotes(r.data)).catch(() => {}); }, [app.id]);
  const addNote = async () => {
    try { const { data } = await api.post(`/employer/applications/${app.id}/notes`, { body }); setNotes((n) => [data, ...n]); setBody(""); } catch (e) { toast.error(errMsg(e)); }
  };
  const setIv = async () => {
    try { const { data } = await api.put(`/employer/applications/${app.id}/interview`, { interview_at: new Date(when).toISOString(), interview_place: place }); onChange(data); toast.success(t("saved_ok")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="card-soft space-y-3 p-4" data-testid="interview-panel">
        <p className="font-bold text-[color:var(--navy)]">{t("interview_set")}</p>
        {app.interview_at && <p className="font-semibold text-amber-800" data-testid="interview-current">{t("interview_on")} {fmtDateTime(app.interview_at, lang)} {app.interview_place && `· ${app.interview_place}`}</p>}
        <input type="datetime-local" className="field" value={when} onChange={(e) => setWhen(e.target.value)} aria-label={t("interview_date")} data-testid="interview-date-input" />
        <input className="field" placeholder={t("interview_place")} value={place} onChange={(e) => setPlace(e.target.value)} data-testid="interview-place-input" />
        <button className="btn-navy !w-full" disabled={!when} onClick={setIv} data-testid="interview-save-btn"><CalendarCheck size={20} />{t("save")}</button>
      </div>
      <div className="card-soft space-y-3 p-4" data-testid="notes-panel">
        <p className="font-bold text-[color:var(--navy)]">{t("notes")}</p>
        <textarea rows={2} className="field !py-3" placeholder={t("note_ph")} value={body} onChange={(e) => setBody(e.target.value)} data-testid="note-input" />
        <button className="btn-ghost !w-full" disabled={!body.trim()} onClick={addNote} data-testid="note-add-btn">{t("add_note")}</button>
        {notes.map((n) => <p key={n.id} className="rounded-xl bg-slate-50 p-3 text-slate-700" data-testid={`note-${n.id}`}>{n.body}<span className="block text-xs text-slate-500">{fmtDateTime(n.created_at, lang)}</span></p>)}
      </div>
    </div>
  );
}

export function CandidateDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { t } = useT();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [app, setApp] = useState(null);
  const [invited, setInvited] = useState(false);
  const appId = sp.get("app");
  useEffect(() => {
    api.get(`/employer/candidates/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
    if (appId) api.get("/employer/applications").then((r) => setApp(r.data.find((a) => a.id === appId) || null)).catch(() => {});
  }, [id, appId]);
  if (err) return <div className="narrow py-10"><Empty icon={Lock} title={err} testid="cand-detail-denied" /></div>;
  if (!c) return <Spinner />;
  const phone = (c.phone || "").replace(/\D/g, "");
  const intl = phone.length === 8 ? `230${phone}` : phone;
  const openCv = async () => { try { const { data } = await api.get(`/employer/candidates/${id}/cv`); window.open(data.url, "_blank", "noopener"); } catch (e) { toast.error(errMsg(e)); } };
  const invite = async () => { try { await api.post(`/employer/candidates/${id}/invite`, {}); setInvited(true); toast.success(t("invited")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="narrow pb-12" data-testid="candidate-detail">
      <button onClick={() => nav(-1)} className="mt-4 inline-flex min-h-[44px] items-center gap-2 font-semibold text-slate-600" data-testid="cand-back-btn"><ArrowLeft size={20} />{t("back")}</button>
      <h1 className="mt-2 text-3xl font-bold text-[color:var(--navy)]" data-testid="cand-name">{c.full_name}</h1>
      {app && <div className="mt-2"><Chip status={app.status} label={t(`est_${app.status}`)} /> <span className="text-slate-600">{app.jobs?.title}</span></div>}
      <div className="mt-5 grid grid-cols-2 gap-3">
        {phone && <a href={`tel:${phone}`} className="btn-navy !w-full" data-testid="cand-call-btn"><Phone size={20} />{t("call")}</a>}
        {phone && <a href={`https://wa.me/${intl}`} target="_blank" rel="noopener noreferrer" className="btn-big w-full bg-[#25D366] text-white" data-testid="cand-whatsapp-btn"><MessageCircle size={20} />{t("whatsapp")}</a>}
        {!app && <button className="btn-orange col-span-2 !w-full" disabled={invited} onClick={invite} data-testid="cand-invite-btn"><Send size={20} />{invited ? t("invited") : t("invite")}</button>}
      </div>
      {app?.match && <div className="card-soft mt-5 p-4"><p className="mb-2 font-bold text-[color:var(--navy)]">{t("why_match")}</p><MatchBar m={app.match} /></div>}
      <div className="card-soft mt-5 divide-y px-4">
        {[[MapPin, "lives", c.location], [ClipboardList, "wants", (c.job_types || []).map((v) => t(`cat_${v}`)).join(", ")], [Sparkles, "experience_l", c.experience && t(`e_${c.experience}`)],
          [Clock, "hours_l", (c.hours || []).map((v) => t(`h_${v}`)).join(", ")], [CalendarCheck, "can_start", c.availability && t(`a_${c.availability}`)],
          [Languages, "speaks", (c.languages || []).map((v) => t(`l_${v}`)).join(", ")], [Mail, "contact", c.email]].map(([Icon, k, v]) => (
          <div key={k} className="flex items-start gap-3 py-3"><Icon size={20} className="mt-0.5 shrink-0 text-[color:var(--orange)]" /><div><p className="text-sm text-slate-500">{t(k)}</p><p className="font-semibold">{v || "—"}</p></div></div>
        ))}
      </div>
      {c.about && <p className="card-soft mt-5 p-4 text-slate-700"><b className="block text-[color:var(--navy)]">{t("about_me")}</b>{c.about}</p>}
      <div className="mt-5 space-y-3">
        {c.has_file_cv && <button className="btn-ghost !w-full" onClick={openCv} data-testid="cand-cv-file-btn"><FileText size={20} />{t("cv_file")}</button>}
        {c.ai_cv ? <pre className="card-soft whitespace-pre-wrap p-4 font-sans text-sm text-slate-800" data-testid="cand-ai-cv">{c.ai_cv}</pre> : !c.has_file_cv && <p className="rounded-xl bg-slate-50 p-4 text-slate-600" data-testid="cand-no-cv">{t("no_cv")}</p>}
      </div>
      {app && <div className="mt-5 space-y-4"><StatusPanel app={app} onChange={(d) => setApp((a) => ({ ...a, ...d }))} /><InterviewAndNotes app={app} onChange={(d) => setApp((a) => ({ ...a, ...d }))} /></div>}
    </div>
  );
}
